'use client';

import { RotateCcw, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { Handling } from '@/lib/services/matter-handling';
import { HandedOverSummary } from './handed-over-summary';
import { useInstitutionHandling } from './use-institution-handling';

/**
 * What the institution sheet shows INSTEAD of the letter composer once the work has
 * been handed to someone outside Vale (VALE-1760).
 *
 * **It replaces, rather than decorates, and that is the whole point.** Before this,
 * handing an institution over left the composer fully live, still offering *Send
 * Notification* — which `ExternalHandlingSendGuardService` then refused. A UI that
 * invites an action the backend is guaranteed to reject is worse than one that
 * handles the rejection well, because the lawyer has already composed the letter by
 * the time they learn it cannot go. Removing the composer makes the refusal
 * unreachable instead of merely handled, so there is no Send button anywhere in
 * this subtree by construction.
 *
 * Both correspondence panels go, not just the draft editor: the thread view offers
 * follow-up affordances of its own, and the header's *Open conversation* already
 * keeps the history one click away for an institution handed over mid-flight.
 *
 * Take-back is the primary action and edit the secondary one. Edit is kept
 * deliberately (owner decision, 2026-09-18): take-back clears the note and the
 * expected-by date server-side, so correcting a date via take-back-then-re-hand
 * would silently discard the note the firm wrote.
 */
export function InstitutionHandedOverPanel({
  matterId,
  institutionId,
  institutionName,
  handling
}: {
  matterId: number;
  institutionId: number;
  institutionName: string;
  handling: Handling | null | undefined;
}) {
  const { isPending, openDialog, takeBack, dialog } = useInstitutionHandling({
    matterId,
    institutionId,
    institutionName,
    handling
  });

  // The caller gates on the same condition, so this is a type narrowing rather
  // than a second decision about when the panel applies.
  if (!handling?.handledBy) return null;

  return (
    <div
      /*
        Top-aligned, not centred in the sheet's full height (VALE-1854).

        `justify-center` with `flex-1` put this five-line message in the middle of
        a very tall panel, so the institution sheet opened on a screen of empty
        space with a paragraph floating in it — it read as a page that had failed
        to load rather than as a deliberate state. The sheet is as tall as the
        viewport whatever the content, so there is no amount of copy that makes
        centring work here.
      */
      className="flex flex-col items-center gap-6 px-6 pt-16 pb-12 text-center"
      data-testid="institution-handed-over-panel"
    >
      <div className="flex flex-col items-center gap-3">
        <span className="bg-info-subtle text-info-subtle-foreground flex h-10 w-10 items-center justify-center rounded-full">
          <UserRound className="h-5 w-5" />
        </span>
        {/* The three facts a handover states, shared with the inventory item
            drawer's panel so the two cannot word them differently (VALE-1854). */}
        <HandedOverSummary
          handling={handling}
          className="flex flex-col gap-1"
        />
      </div>

      {/*
        Take-back is the panel's one real action and carries the button; edit is a
        quieter tier below it rather than beside it. Side by side, an unbordered
        ghost next to a bordered button reads as an unstyled second button rather
        than a deliberate secondary, which is what the VALE-1760 gate flagged.
      */}
      <div className="flex flex-col items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={isPending}
          onClick={takeBack}
          data-testid="institution-handling-take-back"
        >
          <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
          Take back into Vale
        </Button>
        <Button
          tone="muted"
          pad="none"
          density="micro"
          variant="ghost"
          size="auto"
          disabled={isPending}
          onClick={openDialog}
          data-testid="institution-handling-edit"
        >
          Edit handover details
        </Button>
      </div>

      {/* `max-w-sm`, not `xs`: at `xs` this sentence wrapped to a two-word orphan
          line narrower than the block above it. */}
      <p className="text-muted-foreground max-w-sm text-xs">
        Vale will not write to this institution while someone else is handling
        it.
      </p>

      {dialog}
    </div>
  );
}
